"use client";
import { useEffect, useState } from "react";
import {
  getReferralCode,
  getReferralStats,
  ReferralStats,
} from "api/referralApi";
import { Navbar } from "home/Navbar";
import { Footer } from "home/Footer";
import { useAppSelector } from "lib/redux/hooks";
import { selectIsAuthenticationOpen } from "lib/redux/uiSlice";
import { AuthenticationOptions } from "components/AuthenticationOptions";
import { useDispatch } from "react-redux";
import { setIsAuthenticationOpen } from "lib/redux/uiSlice";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ShareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ChartBarIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { toast, ToastContainer } from "react-toastify";

// Bar chart colors for 7 days
const BAR_COLORS = [
  "#1E40AF", // Deep Blue
  "#1C75BC", // Primary Blue
  "#2563EB", // Bright Blue
  "#0891B2", // Cyan Blue
  "#0E7490", // Teal Blue
  "#1D4ED8", // Royal Blue
  "#2563EB", // Sky Blue
];

export default function AffiliatePage() {
  const [referralCode, setReferralCode] = useState<string>("");
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if user is authenticated
    if (typeof window === "undefined") return;
    const accessToken = localStorage.getItem("access-token");

    if (!accessToken) {
      // dispatch(setIsAuthenticationOpen(true));
      setIsLoading(false);
      return;
    }

    const fetchReferralData = async () => {
      try {
        setIsLoading(true);
        const [code, statsData] = await Promise.all([
          getReferralCode(),
          getReferralStats(),
        ]);
        setReferralCode(code);
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching referral data:", error);
        toast.error("Failed to load referral data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralData();
  }, [dispatch]);

  const copyToClipboard = async () => {
    try {
      const referralUrl = `${window.location.origin}/resume-builder?utm_campaign=${referralCode}`;
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Affiliate link copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to copy affiliate link");
    }
  };

  const shareReferralCode = async () => {
    const referralUrl = `${window.location.origin}/resume-builder?utm_campaign=${referralCode}`;
    const shareText = `Join ResumeMate using my referral code and create professional resumes for free! ${referralUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "ResumeMate Referral",
          text: shareText,
          url: referralUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      copyToClipboard();
    }
  };

  // Get top countries with data
  //   const topCountries = stats?.usersByCountry.filter(c => c.users > 0).slice(0, 8) || [];

  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50">
      <div className=" pt-2">
        <Navbar />
      </div>

      <main className="flex-grow py-5 ">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8 text-center">
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
              Affiliate Program
            </h1>
          </div>

          {!referralCode && !stats ? (
            <div className="flex min-h-[500px] items-center justify-center">
              {isLoading ? (
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-gray-300 border-t-[#1C75BC]"></div>
              ) : (
                <div className="mx-auto max-w-md">
                  <div className="rounded-2xl border-2 border-gray-100 bg-white p-8 shadow-xl">
                    <div className="text-center">
                      <div className="mb-6 flex justify-center">
                        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#1C75BC] to-[#27AAE1] shadow-lg">
                          <span className="text-5xl">💰</span>
                        </div>
                      </div>
                      <h2 className="mb-3 text-2xl font-bold text-gray-900">
                        Start Your Affiliate Journey
                      </h2>
                      <p className="mb-6 text-gray-600">
                        Sign in to access your affiliate dashboard and start
                        earning with ResumeMate
                      </p>
                      <button
                        onClick={() => dispatch(setIsAuthenticationOpen(true))}
                        className="flex w-full items-center justify-center gap-3 rounded-lg bg-[#4285F4] px-6 py-4 font-semibold text-white shadow-lg transition-all hover:bg-[#3367D6] hover:shadow-xl"
                      >
                        <svg className="h-6 w-6" viewBox="0 0 24 24">
                          <path
                            fill="#ffffff"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#ffffff"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#ffffff"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#ffffff"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Sign in with Google
                      </button>
                      <p className="mt-4 text-xs text-gray-500">
                        Quick, secure, and takes just a few seconds
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Referral Code and Stats Section - Side by Side on Large Screens */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Referral Code Card */}
                <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[#1C75BC] to-[#27AAE1] p-6 shadow-lg">
                  <div className="mb-4 flex items-center gap-3">
                    <ShareIcon className="h-8 w-8 text-white" />
                    <h2 className="text-2xl font-bold text-white">
                      Your Affiliate Link
                    </h2>
                  </div>
                  <div className="mb-4 rounded-lg bg-white/20 px-6 py-6 backdrop-blur-sm">
                    <p className="break-all text-center font-mono text-xl font-bold tracking-wide text-white lg:text-2xl">
                      https://www.resumemate.io/resume-builder?utm_campaign={referralCode}
                    </p>
                  </div>
                  <p className="mb-6 text-center text-sm text-white/90">
                    Share this code with friends to help them get started with
                    ResumeMate
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={copyToClipboard}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-[#1C75BC] transition-all hover:bg-gray-100 hover:shadow-md"
                    >
                      {copied ? (
                        <>
                          <CheckCircleIcon className="h-5 w-5" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-5 w-5" />
                          Copy Link
                        </>
                      )}
                    </button>
                    <button
                      onClick={shareReferralCode}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white/20 px-6 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30"
                    >
                      <ShareIcon className="h-5 w-5" />
                      Share
                    </button>
                  </div>
                </div>

                {/* Total Referrals Card */}
                <div className="flex items-center justify-center gap-4 rounded-xl bg-white p-6 shadow-md">
                  <div className="rounded-full bg-blue-100 p-4">
                    <UsersIcon className="h-32 w-32 text-[#1C75BC]" />
                  </div>
                  <div>
                    <p className="text-3xl font-medium text-gray-600">
                      Total Referrals
                    </p>
                    <p className="text-7xl font-bold text-gray-900">
                      {stats?.totalUsers || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bar Chart Section */}
              <div className="rounded-xl bg-white p-6 shadow-md">
                <div className="mb-6 flex items-center gap-3">
                  <ChartBarIcon className="h-6 w-6 text-[#1C75BC]" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Last 7 Days Activity
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats?.dailyUsersLast7Days || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                      stroke="#374151"
                      style={{ fontSize: "13px", fontWeight: 500 }}
                    />
                    <YAxis
                      stroke="#374151"
                      style={{ fontSize: "13px", fontWeight: 500 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      labelFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                        });
                      }}
                    />
                    <Bar dataKey="users" radius={[8, 8, 0, 0]}>
                      {stats?.dailyUsersLast7Days.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BAR_COLORS[index % BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* How It Works Section */}
              {/* <div className="rounded-xl bg-white p-6 shadow-md md:p-8">
                <h3 className="mb-6 text-2xl font-bold text-gray-900">
                  How the Referral Program Works
                </h3>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-2xl font-bold text-[#1C75BC]">
                      1
                    </div>
                    <h4 className="mb-2 text-lg font-semibold text-gray-900">
                      Share Your Link
                    </h4>
                    <p className="text-gray-600">
                      Share your unique affiliate link with friends and colleagues.
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-2xl font-bold text-[#1C75BC]">
                      2
                    </div>
                    <h4 className="mb-2 text-lg font-semibold text-gray-900">
                      They Sign Up
                    </h4>
                    <p className="text-gray-600">
                      When someone uses your link , they get started with ResumeMate
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-2xl font-bold text-[#1C75BC]">
                      3
                    </div>
                    <h4 className="mb-2 text-lg font-semibold text-gray-900">
                      Earn Rewards
                    </h4>
                    <p className="text-gray-600">
                      Reach 100 referrals to unlock a free premium subscription!
                    </p>
                  </div>
                </div>
              </div> */}
            </div>
          )}
        </div>
      </main>

      <Footer />
      {isAuthenticationOpen && (
        <AuthenticationOptions redirectTo="/affiliate" />
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
